import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faMeta } from '@fortawesome/free-brands-svg-icons';
import { faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import { Icon, Text, UniversalSelect } from '@feathr/components';

import type { ICreativesFilters, TChannelFilter } from '../ProjectCreativesPage.types';

import * as styles from './ProjectChannelSelect.css';

interface IProps {
  filters: ICreativesFilters;
  setFilters: (filters: ICreativesFilters) => void;
}

interface IChannelOption {
  id: TChannelFilter;
  name: string;
  icon: IconDefinition;
}

function getOptions(t: (key: string) => string): IChannelOption[] {
  return [
    {
      id: 'display',
      name: t('Display'),
      icon: faRectangleAd,
    },
    {
      id: 'meta',
      name: t('Meta'),
      icon: faMeta,
    },
  ];
}

function ChannelOption(props: OptionProps<IChannelOption>): JSX.Element {
  const { data } = props;
  const theme = useMantineTheme();
  return (
    <components.Option {...props}>
      <Flex align={'center'} gap={theme.spacing['2']}>
        <Icon icon={data.icon} size={4} theme={'sky'} />
        <Text.DefaultRegular>{data.name}</Text.DefaultRegular>
      </Flex>
    </components.Option>
  );
}

function ChannelSingleValue(props: SingleValueProps<IChannelOption>): JSX.Element {
  const { data } = props;
  const theme = useMantineTheme();
  return (
    <components.SingleValue {...props}>
      <Flex align={'center'} gap={theme.spacing['2']}>
        <Icon icon={data.icon} size={4} theme={'sky'} />
        <Text.DefaultMedium>{data.name}</Text.DefaultMedium>
      </Flex>
    </components.SingleValue>
  );
}

function ProjectChannelSelect({ filters, setFilters }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const options = getOptions(t);

  const selectedOption = options.find((option) => option.id === filters.channel);

  function handleSelect(option: IChannelOption): void {
    setFilters({
      ...filters,
      channel: option.id,
    });
  }

  function handleClear(): void {
    setFilters({
      ...filters,
      channel: undefined,
    });
  }

  return (
    <UniversalSelect<IChannelOption>
      components={{ Option: ChannelOption, SingleValue: ChannelSingleValue }}
      isClearable={true}
      name={'creative-channel'}
      onClear={handleClear}
      onSelectSingle={handleSelect}
      options={options}
      placeholder={t('Channel')}
      value={selectedOption}
      wrapperClassName={styles.filter}
    />
  );
}

export default ProjectChannelSelect;
